/**
 * Messages API Module
 *
 * Admin/teacher endpoints for school-wide messaging:
 * - Create, list, and delete messages
 * - Preview recipient counts
 *
 * Parent endpoints for viewing messages:
 * - Get messages relevant to a specific child
 */

import { apiRequest, getAuthToken } from './core';
import { getSubdomain } from '@/utils/environment';
import type { components } from '@/types/api.generated';

// ============================================
// Response Types (from OpenAPI-generated types)
// ============================================

export type MessageResponse = components['schemas']['MessageResponse'];
export type CreateMessageRequest = components['schemas']['CreateMessageRequest'];
export type PreviewRecipientsResponse = components['schemas']['PreviewRecipientsResponse'];
export type SmsCapStatusResponse = components['schemas']['SmsCapStatusResponse'];

// ============================================
// Query Types (frontend-only)
// ============================================

export type MessageAudience = 'family' | 'staff';
export type MessageScope = 'block' | 'grade' | 'school' | 'students' | 'all_staff' | 'teachers' | 'support_staff' | 'selected_staff';

export interface ListMessagesQuery {
  scope?: MessageScope;
  blockId?: string;
  isEmergency?: boolean;
  audience?: MessageAudience;
  inbox?: boolean;
  sender?: 'me' | 'others';
  read?: boolean;
  limit?: number;
  offset?: number;
}

export interface PreviewRecipientsQuery {
  audience?: MessageAudience;
  scope: MessageScope;
  blockId?: string;
  gradeLevel?: number;
  studentIds?: string[];
  staffIds?: string[];
  isEmergency: boolean;
}

// ============================================
// API Functions
// ============================================

function buildQueryString(params: Record<string, string | undefined>): string {
  const filtered = Object.entries(params)
    .filter((entry): entry is [string, string] => entry[1] !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return filtered ? `?${filtered}` : '';
}

export const messagesApi = {
  /**
   * Get all messages (admin: all; teacher: own messages)
   */
  getAll: async (query: ListMessagesQuery = {}) => {
    const qs = buildQueryString({
      scope: query.scope,
      blockId: query.blockId,
      isEmergency: query.isEmergency !== undefined ? String(query.isEmergency) : undefined,
      audience: query.audience,
      inbox: query.inbox ? 'true' : undefined,
      sender: query.sender,
      read: query.read !== undefined ? String(query.read) : undefined,
      limit: query.limit?.toString(),
      offset: query.offset?.toString(),
    });
    return apiRequest<{ success: boolean; data: { messages: MessageResponse[]; total: number; unreadCount?: number } }>(
      `/api/messages${qs}`
    );
  },

  /**
   * Create a new message
   */
  create: async (data: CreateMessageRequest) => {
    return apiRequest<{ success: boolean; data: MessageResponse; recipientCount?: number }>(
      '/api/messages',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Delete a message (soft delete)
   */
  delete: async (id: string) => {
    return apiRequest<{ success: boolean; message: string }>(`/api/messages/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Send a draft message (after attachments have been uploaded)
   */
  send: async (id: string) => {
    return apiRequest<{ success: boolean; data: MessageResponse; recipientCount?: number }>(
      `/api/messages/${id}/send`,
      { method: 'POST' }
    );
  },

  /**
   * Retry delivery for a failed message
   */
  retry: async (id: string) => {
    return apiRequest<{ success: boolean; data: MessageResponse }>(
      `/api/messages/${id}/retry`,
      { method: 'POST' }
    );
  },

  /**
   * Preview recipient counts for a given scope
   * Used by compose modal for recipient preview.
   */
  previewRecipients: async (data: PreviewRecipientsQuery) => {
    return apiRequest<{ success: boolean; data: PreviewRecipientsResponse }>(
      '/api/messages/preview-recipients',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Get SMS cap status (weekly cap remaining, emergency count).
   * Used by compose modal for schoolwide/grade scope.
   */
  getSmsCapStatus: async () => {
    return apiRequest<{ success: boolean; data: SmsCapStatusResponse }>(
      '/api/messages/sms-cap-status'
    );
  },

  /**
   * Mark specific messages as read
   */
  markRead: async (messageIds: string[]) => {
    return apiRequest<{ success: boolean; data: { markedCount: number } }>(
      '/api/messages/mark-read',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageIds }),
      }
    );
  },

  /**
   * Mark all inbox messages as read
   */
  markAllRead: async () => {
    return apiRequest<{ success: boolean; data: { markedCount: number } }>(
      '/api/messages/mark-all-read',
      { method: 'POST' }
    );
  },

  /**
   * Upload attachment to a message
   */
  uploadAttachment: async (messageId: string, file: File) => {
    const formData = new FormData();
    formData.append('attachment', file);

    const accessToken = await getAuthToken();
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const subdomain = getSubdomain();
    if (subdomain) {
      headers['X-Tenant-Subdomain'] = subdomain;
    }

    const response = await fetch(`/api/files/message/${messageId}/attachment`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(errorData.error || `Upload failed with status ${response.status}`);
    }

    return response.json() as Promise<{
      success: boolean;
      data: { fileId: string; url: string; originalFilename: string; mimeType: string; fileSize: number };
    }>;
  },

  /**
   * Delete a message attachment
   */
  deleteAttachment: async (messageId: string, fileId: string) => {
    return apiRequest<{ success: boolean; message: string }>(
      `/api/files/message/${messageId}/attachment/${fileId}`,
      { method: 'DELETE' }
    );
  },
};

// ============================================
// Parent Message Types
// ============================================

export interface ParentRelevantStudent {
  id: string;
  firstName: string;
  lastName: string;
  photoFileId: string | null;
  photoUrl: string | null;
}

export type ParentUnifiedMessage = MessageResponse & {
  relevantStudents: ParentRelevantStudent[];
};

// ============================================
// Parent Messages API
// ============================================

/**
 * Parent Messages API
 * Messages visible to parents for a specific child
 */
export const parentMessagesApi = {
  /**
   * Get combined unread message count across all children
   */
  getUnreadCount: async () => {
    return apiRequest<{ success: boolean; data: { unreadCount: number } }>(
      '/api/parent/messages/unread-count'
    );
  },

  /**
   * Get unified messages across all children (deduplicated)
   * Each message includes relevantStudents indicating which children it applies to.
   */
  getUnifiedMessages: async (params?: { limit?: number; offset?: number; read?: boolean; studentId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    if (params?.read !== undefined) queryParams.append('read', String(params.read));
    if (params?.studentId) queryParams.append('studentId', String(params.studentId));
    const queryString = queryParams.toString();

    return apiRequest<{
      success: boolean;
      data: {
        messages: ParentUnifiedMessage[];
        total: number;
        unreadCount: number;
      };
    }>(`/api/parent/messages${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Mark specific messages as read
   */
  markRead: async (messageIds: string[]) => {
    return apiRequest<{ success: boolean; data: { markedCount: number } }>(
      '/api/parent/messages/mark-read',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageIds }),
      }
    );
  },

  /**
   * Mark all visible messages as read
   */
  markAllRead: async () => {
    return apiRequest<{ success: boolean; data: { markedCount: number } }>(
      '/api/parent/messages/mark-all-read',
      { method: 'POST' }
    );
  },

  /**
   * Get messages relevant to a specific child
   * @deprecated Use getUnifiedMessages with studentId param instead
   */
  getMessages: async (studentId: string, params?: { limit?: number; offset?: number; read?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    if (params?.read !== undefined) queryParams.append('read', String(params.read));
    const queryString = queryParams.toString();

    return apiRequest<{
      success: boolean;
      data: {
        messages: MessageResponse[];
        total: number;
        unreadCount: number;
      };
    }>(`/api/parent/messages/${studentId}${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  },
};
